import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Heart, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { isDevelopment } from '@/utils/environmentUtils';
import Dashboard from './Dashboard';

const Callback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing authentication...');
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed.current) {
      return;
    }
    hasProcessed.current = true;

    const handleCallback = async () => {
      try {
        console.log('[Callback] Handling OAuth callback (initial)');

        // Check for token parameter first (from server redirect after successful token exchange)
        // This is a Google Drive OAuth token, NOT an Auth0 token
        const tokenParam = searchParams.get('token');
        if (tokenParam) {
          console.log('[Callback] Found Google Drive token parameter in URL');
          try {
            // Token is double-encoded JSON: first decode the URL encoding, then parse JSON
            const decodedToken = decodeURIComponent(tokenParam);
            const tokenData = JSON.parse(decodedToken);
            
            if (tokenData.access_token) {
              console.log('[Callback] Parsed Google Drive token data, storing in localStorage');
              // Store token in localStorage for mobileGoogleDriveAuth polling to pick up
              // IMPORTANT: This is google_drive_oauth_token, NOT bearer_token (which is for Auth0)
              localStorage.setItem('google_drive_oauth_token', JSON.stringify({
                access_token: tokenData.access_token,
                expires_in: tokenData.expires_in || 3600
              }));
              console.log('[Callback] Google Drive token stored in localStorage as google_drive_oauth_token');
              
              // Clear any Auth0-related tokens/cookies to prevent confusion
              // We don't want to trigger AuthContext login() for Google Drive OAuth
              
              // Web flow - close popup if this is a popup window
              if (window.opener) {
                // This is a popup - delay closing for debugging
                console.log('[Callback] Popup window detected - will close in 30 seconds for debugging');
                console.log('[Callback] Token stored in localStorage:', localStorage.getItem('google_drive_oauth_token'));
                setTimeout(() => {
                  console.log('[Callback] Closing popup window now');
                  window.close();
                }, 30000); // 30 second delay for debugging
                return;
              }
              
              // Not a popup - show success and navigate
              // DO NOT call login() - this is Google Drive OAuth, not Auth0
              setStatus('success');
              setMessage('Google Drive connected successfully!');
              setTimeout(() => {
                navigate('/dashboard');
              }, 1500);
              return;
            } else {
              console.error('[Callback] Token data missing access_token:', tokenData);
              throw new Error('Invalid token data: missing access_token');
            }
          } catch (parseError) {
            console.error('[Callback] Failed to parse token parameter:', parseError);
            setStatus('error');
            setMessage('Failed to parse authentication token');
            return;
          }
        }

        // Check for OAuth errors first (from Google OAuth redirects)
        const error = searchParams.get('error');
        const errorSubtype = searchParams.get('error_subtype');
        const errorDescription = searchParams.get('error_description');
        
        if (error) {
          console.error('[Callback] OAuth error received:', { error, errorSubtype, errorDescription });
          setStatus('error');
          
          // Handle specific OAuth errors
          let errorMessage = 'Authentication failed';
          
          // Handle combination of error and error_subtype
          if (error === 'interaction_required' && errorSubtype === 'access_denied') {
            errorMessage = 'Access was denied. Please grant the required permissions when prompted.';
          } else if (error === 'interaction_required') {
            errorMessage = 'Please complete the authentication in the popup window and grant the required permissions.';
          } else if (error === 'access_denied') {
            errorMessage = 'Authentication was cancelled or access was denied. Please try again and grant the required permissions.';
          } else if (error === 'consent_required') {
            errorMessage = 'Please grant the required permissions to continue.';
          } else if (errorSubtype) {
            // Use error_subtype for more specific messaging
            if (errorSubtype === 'access_denied') {
              errorMessage = 'Access was denied. Please grant the required permissions.';
            } else {
              errorMessage = `Authentication error: ${error} (${errorSubtype})`;
            }
          } else if (errorDescription) {
            errorMessage = decodeURIComponent(errorDescription);
          } else {
            errorMessage = `Authentication error: ${error}`;
          }
          
          setMessage(errorMessage);
          
          // If this is a popup, close it after a short delay
          if (window.opener) {
            // Store error in localStorage so parent window can detect it
            localStorage.setItem('google_drive_oauth_error', error);
            if (errorSubtype) {
              localStorage.setItem('google_drive_oauth_error_subtype', errorSubtype);
            }
            setTimeout(() => window.close(), 3000);
          }
          
          return;
        }

        // Check if this is a Google Drive OAuth callback (has code and state, but no id_token)
        // Note: On iOS, Universal Links will intercept this before the page loads, so this is only for web users
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const hasIdToken = searchParams.get('id_token') || document.cookie.includes('id_token=');
        
        // If we have code/state but no id_token, this is a Google Drive OAuth callback (web only)
        if (code && state && !hasIdToken) {
          console.log('[Callback] Handling Google Drive OAuth callback (web flow)');
          
          try {
            // Get auth headers for backend call
            const bearerToken = localStorage.getItem('bearer_token');
            if (!bearerToken) {
              throw new Error('Not authenticated with Auth0. Please log in first.');
            }

            // Exchange code for token via backend
            const response = await fetch('/callback', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${bearerToken}`
              },
              body: JSON.stringify({ code, state })
            });

            if (!response.ok) {
              let errorText = '';
              let errorData = null;
              try {
                errorText = await response.text();
                // Try to parse as JSON to get the actual error message
                try {
                  errorData = JSON.parse(errorText);
                } catch (e) {
                  // Not JSON, use as-is
                }
              } catch (e) {
                errorText = `HTTP ${response.status}`;
              }
              
              const errorMessage = errorData?.error || errorText || `Token exchange failed: ${response.status}`;
              console.error('[Callback] Token exchange failed:', {
                status: response.status,
                error: errorMessage,
                errorData
              });
              throw new Error(errorMessage);
            }

            const result = await response.json();
            if (result.access_token) {
              // Store token in localStorage for mobileGoogleDriveAuth polling to pick up
              localStorage.setItem('google_drive_oauth_token', JSON.stringify({
                access_token: result.access_token,
                expires_in: result.expires_in
              }));
              console.log('[Callback] Google Drive token stored in localStorage for polling');
              
              // Web flow - close popup if this is a popup window
              if (window.opener) {
                // This is a popup - delay closing for debugging
                console.log('[Callback] Popup window detected (code/state flow) - will close in 30 seconds for debugging');
                console.log('[Callback] Token stored in localStorage:', localStorage.getItem('google_drive_oauth_token'));
                setTimeout(() => {
                  console.log('[Callback] Closing popup window now');
                  window.close();
                }, 30000); // 30 second delay for debugging
                return;
              }
              
              // Not a popup - show success and navigate
              setStatus('success');
              setMessage('Google Drive connected successfully!');
              setTimeout(() => {
                navigate('/dashboard');
              }, 1500);
              return;
            } else {
              throw new Error('No access token in response');
            }
          } catch (error) {
            console.error('[Callback] Google Drive OAuth error:', error);
            setStatus('error');
            setMessage(error instanceof Error ? error.message : 'Failed to complete Google Drive authentication');
            return;
          }
        }

        // Otherwise, handle Auth0 callback
        // IMPORTANT: Only handle Auth0 if we don't have a Google Drive token parameter
        // The token parameter check above should have returned early, but double-check here
        if (searchParams.get('token')) {
          console.log('[Callback] Token parameter still present, should have been handled above');
          return;
        }

        let idToken: string | undefined;

        const hostname = window.location.hostname;

        console.log('Callback - Current hostname:', hostname);
        console.log('Callback - Is development environment:', isDevelopment());

        // BYPASS: Hardcoded token is disabled - will look for cookie or URL param instead
        // TODO: Re-enable hardcoded token when needed by setting USE_HARDCODED_TOKEN to true
        const USE_HARDCODED_TOKEN = false; // Set to true to re-enable hardcoded token
        
        if (isDevelopment() && USE_HARDCODED_TOKEN) {
          // Use hardcoded token for development
          idToken = 'eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6IlFHS0JoeUJzaEpVZ0w1MGVic1BxdSJ9.eyJnaXZlbl9uYW1lIjoiQmlsbCIsImZhbWlseV9uYW1lIjoiRmlwcHMiLCJuaWNrbmFtZSI6ImJpbGwiLCJuYW1lIjoiQmlsbCBGaXBwcyIsInBpY3R1cmUiOiJodHRwczovL2xoMy5nb29nbGV1c2VyY29udGVudC5jb20vYS9BQ2c4b2NJOFFCY0h5UlBHUl9LY1JkeWRxTnVrakdLOE9hSlpFX2lCS3BTcjhXVGZTRjdVSWlVND1zOTYtYyIsInVwZGF0ZWRfYXQiOiIyMDI1LTExLTAzVDAxOjA2OjE3LjMyNVoiLCJlbWFpbCI6ImJpbGxAemltbWRvdC5jb20iLCJlbWFpbF92ZXJpZmllZCI6dHJ1ZSwiaXNzIjoiaHR0cHM6Ly9hdXRoLnpwYXBlci5jb20vIiwiYXVkIjoiSzN1NmJuRXNNc1JBMHR0b3Z1ZlIxRFNHWHRyU1RpMU4iLCJzdWIiOiJnb29nbGUtb2F1dGgyfDEwNjIwMDg5ODk3OTI4ODgyNDc5NSIsImlhdCI6MTc2MjEzMTk3NywiZXhwIjoxNzYyMTY3OTc3LCJzaWQiOiJpWndadFk5cGdGVVZqM2o3aFpsQ0ZCVjZWNjRYRGdoeCIsImF1dGhfdGltZSI6MTc2MjEzMTk3Nywibm9uY2UiOiJqMXhQblVpMFdNWmplaTd3OUdpb0djdHA2WEdMQmxpZCJ9.PUZN1okpP3lmeP1xtBFEVij8y0-KNs14cqJIdv3ulz1Gt5_GsNhO6VgTI-E9BgQOD8Fzvf4Lb4v0G6GV97n3Ows26YdGIHC_8mcMK0kQ0Reo_UwiCcbBm4EAByZwDOdCniF1DCBclaQx3BO6Q-GGAvYEtdN3X7la75cq-8J32NuHRZlbAEeBZ0-dYwHDVPSaRPzvW6CGXRJ5pvoPFvw17zvEv4cnh5lioKSL2wEy8csFD_EFcPN9Qnhlekx2BIHWwVoo_o4gsP1XaVRmtjBCpUtT95WFgijVppVep3MkiPziaL30meQv-ZgDYH5ek0na8Qfm02cX8T7Na-xFr1phRw';
          console.log('Using hardcoded token for development environment');
        } else {
          // First, try to get id_token from URL parameters (in case auth provider redirects with it)
          const urlParams = new URLSearchParams(window.location.search);
          idToken = urlParams.get('id_token') || undefined;
          
          if (idToken) {
            console.log('Found ID token in URL parameters (first 50 chars):', idToken.substring(0, 50) + '...');
          } else {
            // Get the id_token from cookies (works for both dev and production)
            console.log('All cookies:', document.cookie);
            
            const cookies = document.cookie.split(';').map(cookie => cookie.trim());
            console.log('Parsed cookies:', cookies);
            
            const idTokenCookie = cookies.find(cookie => cookie.startsWith('id_token='));
            console.log('ID token cookie found:', idTokenCookie);
            
            if (idTokenCookie) {
              idToken = decodeURIComponent(idTokenCookie.split('=')[1]);
              console.log('Extracted ID token from cookie (first 50 chars):', idToken?.substring(0, 50) + '...');
            }
          }
        }

        if (!idToken) {
          const storedToken = localStorage.getItem('bearer_token') || undefined;
          if (storedToken) {
            console.log('Using ID token from localStorage fallback (first 50 chars):', storedToken.substring(0, 50) + '...');
            idToken = storedToken;
          }
        }

        if (!idToken) {
          console.error('No ID token found. Cookies:', document.cookie);
          setStatus('error');
          setMessage('No ID token found');
          return;
        }

        // Clear the id_token cookie if it exists (production only)
        if (!isDevelopment()) {
          document.cookie = 'id_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
          console.log('Cleared id_token cookie');
        }

        // Store the ID token as bearer token in localStorage
        localStorage.setItem('bearer_token', idToken);
        
        // Update the AuthContext state
        login();
        
        setStatus('success');
        setMessage('Authentication successful! Loading your dashboard...');

        // Determine post-login redirect target
        const qsRedirect = searchParams.get('post_login_redirect');
        const storedRedirect = sessionStorage.getItem('post_login_redirect');
        const target = qsRedirect || storedRedirect || '/dashboard';

        // Clear stored redirect after using it
        if (storedRedirect) {
          sessionStorage.removeItem('post_login_redirect');
        }

        if (target !== '/dashboard') {
          navigate(target, { replace: true });
        }

      } catch (error) {
        console.error('Callback error:', error);
        setStatus('error');
        setMessage('An error occurred during authentication');
      }
    };

    handleCallback();
  }, [navigate, login]);

  if (status === 'success') {
    return <Dashboard />;
  }

  const isProcessing = status === 'processing';
  const isError = status === 'error';

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Heart className="h-12 w-12 text-primary" />
          </div>
          <CardTitle>healthscores.ai</CardTitle>
          <CardDescription>
            {isProcessing && 'Completing sign in...'}
            {isError && 'Authentication Error'}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            {isProcessing && <Loader2 className="h-4 w-4 animate-spin" />}
            <span className={
              isError ? 'text-destructive' : 'text-muted-foreground'
            }>
              {message}
            </span>
          </div>
          
          {isError && (
            <div className="mt-4">
              <button 
                onClick={() => navigate('/')}
                className="text-primary hover:underline"
              >
                Return to homepage
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Callback;
